import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Package, Globe2, Banknote, Zap, Layers, Scale, Box, AlertOctagon, Truck, MapPin,
  ArrowRight, Check, FileText, ScanSearch,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, fadeUp } from "../components/marketing/Blocks";

const JOURNEY = ["Create Shipment", "Pickup", "Transit", "Delivery", "Delivered"];

const SERVICES = [
  { icon: Package, title: "Domestic Shipping", desc: "Pan-India shipping across serviceable pincodes with multiple courier options." },
  { icon: Globe2, title: "International Shipping", desc: "Cross-border shipping from India with customs information workflows." },
  { icon: Banknote, title: "COD Shipping", desc: "Cash-on-delivery shipments with collection and settlement visibility." },
  { icon: Zap, title: "Express Services", desc: "Faster delivery options where offered by the selected courier service." },
  { icon: Layers, title: "Bulk Shipping", desc: "Process large order volumes via bulk upload and centralized tracking." },
];

const PACKAGING = [
  "Select appropriate packaging",
  "Protect the product with cushioning",
  "Seal the package securely",
  "Attach the correct shipping label",
  "Ensure package details are accurate",
];

const PROHIBITED = [
  "Dangerous goods & hazardous materials",
  "Currency, bullion & negotiable instruments",
  "Items prohibited by applicable law",
  "Courier-restricted categories per partner policy",
];

const DELIVERY_POINTS = [
  { t: "Estimated transit times", d: "Delivery estimates shown at booking are courier estimates and may vary by lane and season." },
  { t: "Delivery attempts", d: "Couriers typically attempt delivery more than once before marking a shipment undelivered." },
  { t: "Serviceability", d: "Serviceability is checked per pincode at booking; availability varies by courier." },
  { t: "Remote locations", d: "Remote or extended-area pincodes may have longer transit times or limited services." },
  { t: "Delays", d: "Weather, network and operational disruptions can delay shipments beyond estimates." },
  { t: "Recipient availability", d: "Please ensure the recipient's address and contact details are accurate and reachable." },
];

const INTL_ITEMS = ["Commercial invoice", "Product description", "HS code", "Customs documentation", "Duties/taxes", "Destination restrictions"];

export default function ShippingPolicy() {
  return (
    <MarketingLayout>
      <div data-testid="shipping-policy-page">
        <MarketingHero kicker="Shipping Policy" title="Ship Right. Pack Right. Deliver Better."
          sub="Understand ShipAny's shipping processes, packaging requirements, delivery procedures and shipment responsibilities." />

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="shipping-journey-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="The journey" title="Shipping Journey" />
            <div className="relative mt-14" data-testid="shipping-journey-flow">
              <div className="absolute left-0 right-0 top-6 hidden h-0.5 rounded bg-white/10 sm:block" />
              <motion.div animate={{ left: ["2%", "90%"] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
                className="absolute -top-2 z-10 hidden h-9 w-9 items-center justify-center rounded-xl border border-[#FF9933]/50 bg-[#071B3A] shadow-[0_6px_20px_rgba(255,153,51,0.35)] sm:flex"
                data-testid="shipping-journey-package">
                <Truck className="h-4 w-4 text-[#FF9933]" />
              </motion.div>
              <div className="relative grid gap-6 sm:grid-cols-5">
                {JOURNEY.map((s, i) => (
                  <motion.div key={s} {...fadeUp} transition={{ duration: 0.45, delay: i * 0.1 }} className="text-center"
                    data-testid={`shipping-journey-step-${i + 1}`}>
                    <span className="relative z-[5] mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-shipany-cyan/40 bg-[#0B1E3D] font-display text-sm font-bold text-shipany-cyan">{i + 1}</span>
                    <p className="mt-3 text-xs font-bold text-white sm:text-sm">{s}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mx-auto mt-8 max-w-md rounded-xl border border-red-400/30 bg-red-500/10 px-5 py-3 text-center text-xs font-semibold text-red-300" data-testid="shipping-journey-ndr-branch">
                Delivery issue? Shipment branches to <span className="font-bold">NDR → Reattempt</span> or, if unresolved, <span className="font-bold">RTO → Return to Seller</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-16 sm:py-20" data-testid="shipping-services-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Services" title="Shipping Services" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
              {SERVICES.map((s, i) => (
                <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
                  data-testid={`shipping-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20"><s.icon className="h-5 w-5 text-shipany-cyan" /></span>
                  <h3 className="font-display mt-3 text-xs font-bold text-white">{s.title}</h3>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="shipping-weight-section">
          <div className="mx-auto max-w-4xl px-5">
            <SectionHead kicker="Billing" title="Weight & Billing" />
            <motion.div {...fadeUp} className="mt-12 grid items-center gap-5 sm:grid-cols-[1fr_auto_1fr]" data-testid="shipping-weight-visual">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                <Scale className="mx-auto h-6 w-6 text-shipany-cyan" />
                <p className="font-display mt-3 text-sm font-bold text-white">Actual Weight</p>
                <p className="mt-1 text-xs text-slate-400">The physical weight of the packed shipment.</p>
              </div>
              <span className="text-center font-display text-lg font-extrabold text-[#FF9933]">VS</span>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                <Box className="mx-auto h-6 w-6 text-[#FF9933]" />
                <p className="font-display mt-3 text-sm font-bold text-white">Volumetric Weight</p>
                <p className="mt-1 text-xs text-slate-400">The space the package occupies, from its dimensions.</p>
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="mx-auto mt-6 max-w-xl rounded-2xl border border-[#10B981]/40 bg-[#10B981]/10 p-6 text-center" data-testid="shipping-chargeable-note">
              <p className="font-display text-sm font-bold text-white">Chargeable Weight</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-300">
                The applicable billing weight is determined according to the relevant courier/service rules. The exact chargeable weight and final price are always shown before you book.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="bg-shipany-navy py-16 sm:py-20" data-testid="shipping-packaging-section">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Pack Your Shipment Properly</h2>
              <p className="mt-3 text-sm text-slate-400">Good packaging protects your products and prevents in-transit damage and disputes.</p>
              <ul className="mt-6 space-y-3">
                {PACKAGING.map((p, i) => (
                  <motion.li key={p} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.07 }}
                    data-testid={`shipping-packaging-${i + 1}`}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-200">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#10B981]/15"><Check className="h-3.5 w-3.5 text-[#10B981]" /></span>
                    {p}
                  </motion.li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Prohibited & Restricted Items</h2>
              <p className="mt-3 text-sm text-slate-400">The following categories must not be shipped through ShipAny.</p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {PROHIBITED.map((p, i) => (
                  <motion.div key={p} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.07 }}
                    data-testid={`shipping-prohibited-${i + 1}`}
                    className="flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3.5">
                    <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    <span className="text-xs font-semibold leading-relaxed text-red-200">{p}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="shipping-delivery-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Delivery" title="Delivery Expectations" />
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {DELIVERY_POINTS.map((d, i) => (
                <motion.div key={d.t} {...fadeUp} transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  data-testid={`shipping-delivery-${d.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <h3 className="font-display text-sm font-bold text-white">{d.t}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{d.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-16 sm:py-20" data-testid="shipping-ndr-section">
          <div className="mx-auto max-w-4xl px-5">
            <SectionHead kicker="Exceptions" title="NDR & RTO" />
            <motion.div {...fadeUp} className="mt-12 flex flex-wrap items-center justify-center gap-2" data-testid="shipping-ndr-flow">
              {["Attempt Failed", "NDR", "Action", "Reattempt"].map((s, i) => (
                <span key={s} className="flex items-center gap-2">
                  <span className="rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-300" data-testid={`shipping-ndr-step-${i + 1}`}>{s}</span>
                  {i < 3 && <ArrowRight className="h-3.5 w-3.5 text-slate-600" />}
                </span>
              ))}
            </motion.div>
            <motion.div {...fadeUp} className="mt-5 flex flex-wrap items-center justify-center gap-2" data-testid="shipping-rto-flow">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">If unsuccessful:</span>
              {["RTO Initiated", "Return Transit", "Seller"].map((s, i) => (
                <span key={s} className="flex items-center gap-2">
                  <span className="rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-2 text-xs font-semibold text-red-300" data-testid={`shipping-rto-step-${i + 1}`}>{s}</span>
                  {i < 2 && <ArrowRight className="h-3.5 w-3.5 text-slate-600" />}
                </span>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="shipping-intl-section">
          <div className="mx-auto max-w-5xl px-5">
            <SectionHead kicker="Cross-border" title="International Shipping"
              sub="International shipments involve additional documentation and destination-country requirements." />
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {INTL_ITEMS.map((t, i) => (
                <motion.div key={t} {...fadeUp} transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                  data-testid={`shipping-intl-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
                  <FileText className="h-4 w-4 shrink-0 text-shipany-cyan" />
                  <span className="text-xs font-semibold text-slate-200">{t}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-slate-500" data-testid="shipping-intl-note">
              Rates, transit times, serviceability, duties, taxes and customs requirements may vary based on destination, shipment characteristics, carrier and applicable regulations.
            </p>
          </div>
        </section>

        <section className="relative overflow-hidden bg-gradient-to-br from-shipany-blue via-[#0A5CA8] to-[#10B981] py-16 sm:py-20" data-testid="shipping-track-cta">
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#12C8F5]/20 blur-3xl" />
          <div className="relative mx-auto max-w-3xl px-5 text-center">
            <motion.div {...fadeUp}>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><ScanSearch className="h-6 w-6 text-white" /></span>
              <h2 className="font-display mt-5 text-2xl font-bold tracking-tight text-white sm:text-3xl">Know Where Your Shipment Is</h2>
              <p className="mt-3 text-sm text-white/80 sm:text-base">Real-time tracking from pickup to proof of delivery — no login required.</p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link to="/track-shipment" data-testid="shipping-track-btn"
                  className="flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-3.5 font-display text-sm font-bold text-shipany-blue transition-all hover:shadow-[0_10px_36px_rgba(255,255,255,0.35)]">
                  Track Shipment <MapPin className="h-4 w-4" />
                </Link>
                <Link to="/signup" data-testid="shipping-start-btn"
                  className="flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/10">
                  Start Shipping
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
